import type { Params, UrlSegment } from '@angular/router';

import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { ISbStoryData } from '@storyblok/js';

import type { Language } from '@valk-nx/core/lib/core';
import { fallbackLanguage, negativeHeader$ } from '@valk-nx/core/lib/core';
import type { CheckedOption } from '@valk-nx/core/lib/interfaces/form.interface';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { LanguageSwitchHelper } from '@valk-nx/helpers/lib/language/language-switch';
import type {
	BynderImage,
	FormCheckboxInterface,
	FormHeaderInterface,
	FormInputInterface,
	FormSelectInterface,
	ISbStoryBlok,
	Link,
	RootInterface,
	SbFooterMenu,
	StoryblokLink,
	StoryblokMarkdownLink,
	TranslatedStoryblokLink,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { removeTrailingSlash } from './sitemap.helper';

export interface BlocksInterface
	extends FormInputInterface, FormSelectInterface {
	id: string;
	_uid: string;
}

export class StoryblokHelper {
	static getStoryblokParameters(
		urlSegments: UrlSegment[],
		queryParams: Params,
		rootLanguage: string = fallbackLanguage,
	): RootInterface {
		const isDraft = Boolean(queryParams['_storyblok']);
		const isLanguageInPath =
			urlSegments.length &&
			LanguageSwitchHelper.isLanguageCode(urlSegments[0].path);
		const language = (
			isLanguageInPath ? urlSegments[0].path : rootLanguage
		) as Language;

		// Create a copy of the array to avoid mutating the original
		const segmentsCopy = [...urlSegments];
		const slug =
			segmentsCopy
				.splice(isLanguageInPath ? 1 : 0)
				.map((urlSegment) => urlSegment.path)
				.join('/') || 'home';

		return { slug, isDraft, language };
	}

	static mapFooterMenu(menu: SbFooterMenu[] | undefined): {
		title: string;
		items: { title: string; url: string; target: string }[];
	}[] {
		if (!menu) {
			return [];
		}

		return menu.map((menuItem: SbFooterMenu) => {
			return {
				title: menuItem.title,
				items: menuItem.items.map((item) => {
					return {
						title: item.title,
						url: item.link.cached_url,
						target: item.link.target ?? '_self',
					};
				}),
			};
		});
	}

	static replaceStoryblokUrls(
		storyData: ISbStoryData,
		host: string,
	): ISbStoryData {
		const jsonString = JSON.stringify(storyData);
		const updatedString = jsonString.replace(
			/https:\/\/a\.storyblok\.com/g,
			host,
		);
		return JSON.parse(updatedString);
	}

	static mapLanguageUrlSlug(
		storyData: ISbStoryData,
		storyLinks: TranslatedStoryblokLink[],
		language: string,
		searchKey = 'link',
		rootLanguage: string = fallbackLanguage,
	): ISbStoryData {
		const translatedStoryData = JSON.stringify(storyData, (key, value) => {
			if (StoryblokHelper.isMarkdownLink(key, value)) {
				const translatedLinkAttrs =
					StoryblokHelper.getMarkdownTranslatedUrl(
						value[0].attrs,
						storyLinks,
						language,
						rootLanguage,
					);

				return [
					{
						...value[0],
						attrs: translatedLinkAttrs,
					},
					...value.slice(1),
				];
			}
			if (
				(key.toLowerCase().endsWith('link') ||
					key.toLowerCase().endsWith('url') ||
					key.toLowerCase().endsWith(searchKey)) &&
				value?.cached_url
			) {
				return StoryblokHelper.getTranslatedUrl(
					value,
					storyLinks,
					language,
					rootLanguage,
				);
			}
			return value;
		});
		return JSON.parse(translatedStoryData);
	}

	private static isMarkdownLink(key: string, value: unknown): boolean {
		return (
			key.toLowerCase().endsWith('marks') &&
			Array.isArray(value) &&
			value[0]?.type === 'link'
		);
	}

	static getTranslatedUrl(
		link: StoryblokLink,
		storyLinks: TranslatedStoryblokLink[],
		language: string,
		rootLanguage: string = fallbackLanguage,
	): StoryblokLink {
		const url = link.cached_url;

		// Preserve external links
		if (url.startsWith('http')) {
			return link;
		}
		// Make sure if we have the word home in the route to remove it and default to the slash
		if (url.endsWith('home')) {
			return {
				...link,
				cached_url: url.replace(/\/?home$/, ''),
			};
		}

		const translatedLinks = storyLinks.find((storyLink) => {
			return storyLink.uuid === link.id;
		});

		if (!translatedLinks) return link;

		if (language === rootLanguage) {
			return {
				...link,
				cached_url: removeTrailingSlash(
					link.cached_url.replace(
						/[^/]+\/?$/,
						`${translatedLinks.slug}/`,
					),
				),
			};
		}

		const translatedUrl = translatedLinks.alternates.find(
			(alternate) => alternate.lang === language,
		);

		return {
			...link,
			cached_url: translatedUrl
				? `/${language}/${translatedUrl.path.replace(/\/$/, '')}`
				: link.cached_url,
		};
	}

	static getMarkdownTranslatedUrl(
		link: StoryblokMarkdownLink,
		storyLinks: TranslatedStoryblokLink[],
		language: string,
		rootLanguage: string = fallbackLanguage,
	): StoryblokMarkdownLink {
		const modifiedLink = { ...link, href: removeTrailingSlash(link.href) };
		const url = modifiedLink.href;

		// Preserve external links
		if (url?.startsWith('http')) {
			return modifiedLink;
		}

		if (url?.endsWith('home')) {
			const newHomeUrl = language !== rootLanguage ? `/${language}` : '/';
			return {
				...modifiedLink,
				href: newHomeUrl,
			};
		}

		const translatedLinks = storyLinks.find(
			(storyLink) => storyLink.uuid === modifiedLink.uuid,
		);

		if (!translatedLinks) return modifiedLink;

		const translatedUrl = translatedLinks.alternates.find(
			(alternate) => alternate.lang === language,
		);

		if (!translatedUrl) return modifiedLink;

		return {
			...modifiedLink,
			href: `${translatedUrl.lang}/${translatedUrl.path.replace(/\/$/, '')}`,
		};
	}

	static getUrlFromStoryblokLink(link: Link | undefined): string | undefined {
		if (link && (link.id || link.url)) {
			return link.cached_url;
		}
		return undefined;
	}

	static mapBynderToPhotoInterface(
		bynder: BynderImage,
		alt?: string,
	): PhotoInterface {
		const photoAlt = alt ? { alt } : null;
		return {
			...photoAlt,
			src: bynder.files?.transformBaseUrl?.url ?? '',
		};
	}

	static buildFormlyData(
		inputs:
			| FormInputInterface[]
			| FormHeaderInterface[]
			| FormSelectInterface[]
			| FormCheckboxInterface[],
	): FormlyFieldConfig[] {
		return inputs.map(
			(
				input: Partial<
					FormInputInterface &
						FormHeaderInterface &
						FormSelectInterface &
						FormCheckboxInterface & { id: string }
				>,
				index: number,
			) => {
				const tooltipObject = input.tooltipContent
					? {
							props: {
								tooltip: {
									alignment: 'top-start',
									content: input.tooltipContent,
									ariaLabel: input.tooltipLabel,
								},
							},
							wrappers: ['tooltip', 'label'],
						}
					: null;

				const inputType =
					input.type === 'tel' && input.optional
						? 'text'
						: input.type;

				const config = {
					id: input.id,
					key: input.id,
					props: {
						forceInitialValue: true,
						label: input.label,
						labelExtras: {
							contentWidth: '',
							helperText: input.helperText,
							requiredHelperText: input.optional
								? 'global.optional'
								: '',
							alignment: 'top-aligned-inline',
							width: '',
							noPadding: false,
							withMarginTop: index === 0,
						},
						optional: input.optional,
						required: !input.optional,
						translate: false,
						...tooltipObject?.props,
					},
					type: inputType,
					wrappers: tooltipObject?.wrappers || ['label'],
				};

				switch (input.component) {
					case 'formDivider': {
						return {
							className: 'section-label',
							type: 'divider',
						};
					}
					case 'formHeader': {
						return {
							props: {
								alignment: 'center',
								gapSize: 'large',
								label: input.header,
							},
							type: 'heading',
						};
					}
					case 'formSelect': {
						const optionsObject =
							StoryblokHelper.parseStoryblokSelectOptions(
								input as FormSelectInterface,
							);

						return {
							...config,
							props: {
								...config.props,
								...optionsObject,
							},
						};
					}
					case 'formCheckbox': {
						return {
							...config,
							props: {
								...config.props,
								checkboxLabel: input.label,
								label: '',
								labelExtras: {
									...config.props.labelExtras,
									requiredHelperText: '',
								},
								showAsToggle: input.toggle,
							},
							type: 'checkbox',
						};
					}
					default: {
						const icon =
							input.icon && input.iconPosition
								? {
										icon: input.icon,
										iconPosition: input.iconPosition,
									}
								: null;

						const maskedOptions = input.mask
							? {
									maskOptions: {
										mask: input.mask,
										prefix: input.maskPrefix,
										showMaskedType: true,
									},
								}
							: null;
						return {
							...config,
							props: {
								...config.props,
								...icon,
								...maskedOptions,
								placeholder: input.placeholder ?? '',
							},
						};
					}
				}
			},
		);
	}

	static parsedFormData(
		data: Record<string, string | CheckedOption[]>,
		blocks: Partial<BlocksInterface[]>,
	): ({
		id: string;
		label: string;
		value: string | CheckedOption[];
	} | null)[] {
		return Object.keys(data).map((key) => {
			const block = (blocks as Partial<BlocksInterface>[]).find(
				(block: Partial<BlocksInterface>) => block.id === key,
			) as BlocksInterface;
			let value = data[key] || '-';

			if (!block) {
				return null;
			}

			if (Array.isArray(value)) {
				value = value
					.filter((option) => option.checked)
					.map((option) => option.inputId)
					.join(', ');
			}

			if (block.component === 'formSelect') {
				if (typeof data[key] === 'string') {
					value = block.options.find(
						(option) => option.value === data[key],
					)?.label as string | CheckedOption[];
				} else if (data[key]?.length) {
					const keys = data[key] as unknown as CheckedOption[];
					const optionValues = keys
						.map((val) => {
							if (val.checked) {
								return val.inputId;
							}
							return null;
						})
						.filter((value) => value);

					value = optionValues
						.map(
							(optionValue) =>
								block.options.find(
									(option) => option.value === optionValue,
								)?.label,
						)
						.toString();
				}
			}

			if (typeof value === 'boolean') {
				value = (value as boolean).toString();
			}

			return {
				id: key,
				label: block.label,
				value,
			};
		});
	}

	static parseEmailRecipientFormData(
		blocks: BlocksInterface[],
		formData: {
			id: string;
			label: string;
			value: string | CheckedOption[];
		}[],
	): string[] {
		return blocks
			.filter((block) => block.sendConfirmation && block.type === 'email')
			.map((block) => {
				const matchingData = formData.find(
					(data) => data.id === block.id,
				);
				return matchingData!.value as string;
			})
			.filter((value) => value !== null);
	}

	private static parseStoryblokSelectOptions(input: FormSelectInterface) {
		switch (input.type) {
			case 'checkboxGroup': {
				const options = input.options.map((option) => {
					return {
						label: option.label,
						inputId: option.value,
					};
				});
				return { checkboxOptions: options };
			}
			case 'radioGroup': {
				const options = input.options.map((option) => {
					return {
						label: option.label,
						inputId: option.value,
					};
				});
				return { radioOptions: options };
			}
			case 'segmentedControl': {
				const options = input.options.map((option) => {
					return {
						name: option.label,
						value: option.value,
					};
				});
				return { segmentedOptions: options };
			}
			case 'select':
			default: {
				const options = input.options.map((option) => {
					return {
						label: option.label,
						value: option.value,
					};
				});
				return { selectOptions: options };
			}
		}
	}

	static checkNegativeHeader(content: { component: string }) {
		let useNegative = false;

		if (content.component) {
			useNegative =
				content.component === 'carousel' ||
				content.component === 'carouselWithAvailabilityWidget' ||
				content.component === 'carouselWithWidget';
		}
		negativeHeader$.next(useNegative);
	}

	/*
	 * Extract all accordion items from the provided Storyblok content nested array(s).
	 * Returns an array of objects containing question and answer pairs.
	 */
	static getAllAccordionItems(
		content: ISbStoryBlok[],
	): { question: string; answer: string }[] {
		const accordionItems: { question: string; answer: string }[] = [];

		const extractTextFromContent = (content: unknown): string => {
			if (!content || typeof content !== 'object') {
				return '';
			}

			const contentObj = content as Record<string, unknown>;
			const textParts: string[] = [];

			if (Array.isArray(contentObj['content'])) {
				contentObj['content'].forEach((item: unknown) => {
					textParts.push(extractTextFromContent(item));
				});
			}

			if (typeof contentObj['text'] === 'string') {
				textParts.push(contentObj['text']);
			}

			return textParts
				.filter((text) => text.trim())
				.join(' ')
				.replace(/\s+/g, ' ')
				.trim();
		};

		const searchForAccordionItems = (obj: unknown): void => {
			/* v8 ignore start */
			if (!obj || typeof obj !== 'object') {
				return;
			}
			/* v8 ignore stop */

			const objRecord = obj as Record<string, unknown>;

			if (
				objRecord['component'] === 'accordionItem' &&
				objRecord['title']
			) {
				const question = objRecord['title'] as string;
				const answer = extractTextFromContent(objRecord['content']);

				if (question && answer) {
					accordionItems.push({ question, answer });
				}
			}

			Object.values(objRecord).forEach((value) => {
				if (Array.isArray(value)) {
					value.forEach(searchForAccordionItems);
				} else if (value !== null && typeof value === 'object') {
					searchForAccordionItems(value);
				}
			});
		};

		searchForAccordionItems(content);
		return accordionItems;
	}
}
